import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, Clock3, Menu, RefreshCw, Search, Send, Store, Tickets, UserRound, X } from "lucide-react";
import { moduleConfigs } from "../operations/moduleConfigs";
import { moduleDataSource } from "../operations/moduleDataSource";
import type { ModuleRow } from "../operations/types";

const config = moduleConfigs.automation!;
const platformOrder = ["天猫", "京东", "抖音", "拼多多", "小红书", "快手"];

function text(value: unknown) {
  return value === undefined || value === null ? "" : String(value);
}

function platformOf(row: ModuleRow) {
  return text(row.platform) || text(row.store).split("/")[0]?.trim() || "其他";
}

function storeOf(row: ModuleRow) {
  return text(row.storeName) || text(row.store).split("/").slice(1).join("/").trim() || "未识别店铺";
}

function minutesOf(row: ModuleRow) {
  if (typeof row.remainingMinutes === "number") return row.remainingMinutes;
  if (text(row.status) === "已超时") return -1;
  const deadline = text(row.deadline);
  const value = Number(deadline.match(/[0-9]+/)?.[0] ?? 120);
  return deadline.includes("分钟") ? value : value * 60;
}

function urgencyOf(row: ModuleRow) {
  if (text(row.status) === "已完成") return "completed";
  const minutes = minutesOf(row);
  if (text(row.status) === "已超时" || minutes <= 0) return "overdue";
  if (minutes <= 30) return "soon";
  if (minutes <= 120) return "watch";
  return "normal";
}

function urgencyLabel(row: ModuleRow) {
  const urgency = urgencyOf(row);
  if (urgency === "overdue") return "已超时";
  if (urgency === "soon") return "即将超时";
  if (urgency === "watch") return "需关注";
  if (urgency === "completed") return "已完成";
  return "正常";
}

function syncLabel(row: ModuleRow) {
  if (row.platformSync) return text(row.platformSync);
  return text(row.status) === "已完成" ? "待平台回写" : "待回写";
}

export function ServiceTicketsPage({ onMenuOpen }: { onMenuOpen: () => void }) {
  const [rows, setRows] = useState<ModuleRow[]>([]);
  const [search, setSearch] = useState("");
  const [platform, setPlatform] = useState("");
  const [urgency, setUrgency] = useState("");
  const [status, setStatus] = useState("");
  const [selected, setSelected] = useState<ModuleRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");

  useEffect(() => { void moduleDataSource.load(config).then(setRows); }, []);
  useEffect(() => { if (rows.length) void moduleDataSource.save(config, rows); }, [rows]);

  const overdue = rows.filter((row) => urgencyOf(row) === "overdue").length;
  const soon = rows.filter((row) => urgencyOf(row) === "soon").length;
  const pending = rows.filter((row) => !["已完成"].includes(text(row.status))).length;
  const synced = rows.filter((row) => syncLabel(row) === "已同步").length;
  const platforms = platformOrder.filter((item) => rows.some((row) => platformOf(row) === item));

  const visibleRows = useMemo(() => rows
    .filter((row) => !platform || platformOf(row) === platform)
    .filter((row) => !urgency || urgencyOf(row) === urgency)
    .filter((row) => !status || text(row.status) === status)
    .filter((row) => {
      const query = search.trim().toLowerCase();
      return !query || Object.values(row).some((value) => text(value).toLowerCase().includes(query));
    })
    .sort((a, b) => {
      const rank = { overdue: 0, soon: 1, watch: 2, normal: 3, completed: 4 } as const;
      return rank[urgencyOf(a)] - rank[urgencyOf(b)] || minutesOf(a) - minutesOf(b);
    }), [platform, rows, search, status, urgency]);

  const showNotice = (message: string) => {
    setNotice(message);
    window.setTimeout(() => setNotice(""), 2600);
  };

  const refreshTickets = async () => {
    setBusy(true);
    const next = await moduleDataSource.sync(config, rows);
    setRows(next);
    window.setTimeout(() => setBusy(false), 500);
    showNotice(next.length > rows.length ? `已聚合新增 ${next.length - rows.length} 条平台工单` : "全部平台工单已刷新");
  };

  const startTicket = (row: ModuleRow) => {
    const updated: ModuleRow = { ...row, status: "处理中", agent: text(row.agent) === "待分派" ? "售后组" : text(row.agent), platformSync: "待回写" };
    setRows((current) => current.map((item) => item.id === row.id ? updated : item));
    setSelected(updated);
    showNotice(`${text(row.ticket)} 已进入处理`);
  };

  const completeAndSync = (row: ModuleRow) => {
    const syncing = { ...row, status: "已完成", platformSync: "回写中" };
    setRows((current) => current.map((item) => item.id === row.id ? syncing : item));
    setSelected(syncing);
    showNotice("处理结果正在回写平台");
    window.setTimeout(() => {
      setRows((current) => current.map((item) => item.id === row.id ? { ...item, platformSync: "待真实平台回写" } : item));
      setSelected((current) => current?.id === row.id ? { ...current, platformSync: "待真实平台回写" } : current);
      showNotice("处理结果已进入平台回写队列，等待真实授权接口");
    }, 900);
  };

  return (
    <main className="main serviceTicketsMain">
      <header className="topbar"><button className="menuButton" aria-label="打开导航" onClick={onMenuOpen}><Menu /></button><strong>平台服务工单</strong><span>跨平台统一处理与超时提醒</span></header>
      <div className="serviceTicketsContent">
        <section className="serviceTicketsHead"><div><h1>统一工单工作台</h1><p>所有平台工单集中处理，优先显示已超时和即将超时任务。</p></div><button className="button primary" disabled={busy} onClick={() => void refreshTickets()}><RefreshCw className={busy ? "spin" : ""} />{busy ? "聚合中..." : "聚合全部平台工单"}</button></section>

        <section className="serviceTicketInfo"><Send /><div><strong>处理完成后进入平台回写队列</strong><span>当前未配置各平台写入授权，页面会保留“待真实平台回写”状态；接入授权后再自动写回平台。</span></div></section>
        {(overdue > 0 || soon > 0) && <section className="serviceTicketAlert"><AlertTriangle /><div><strong>当前有 {overdue} 条已超时、{soon} 条将在 30 分钟内超时</strong><span>已自动排到列表最前面，请优先处理并回写平台。</span></div><button onClick={() => { setPlatform(""); setStatus(""); setUrgency(overdue ? "overdue" : "soon"); }}>查看紧急工单</button></section>}

        <nav className="serviceTicketPlatforms" aria-label="工单平台筛选"><button className={!platform ? "active" : ""} onClick={() => setPlatform("")}><Tickets /><span><strong>全部平台</strong><small>{rows.length} 条</small></span></button>{platforms.map((item) => <button key={item} className={platform === item ? "active" : ""} onClick={() => setPlatform(item)}><Store /><span><strong>{item}</strong><small>{rows.filter((row) => platformOf(row) === item).length} 条</small></span></button>)}</nav>

        <section className="serviceTicketKpis">
          <button onClick={() => setUrgency("")}><span>全部工单</span><strong>{rows.length}</strong><small>跨平台统一聚合</small><Tickets /></button>
          <button className="amber" onClick={() => setUrgency("soon")}><span>即将超时</span><strong>{soon}</strong><small>剩余不超过 30 分钟</small><Clock3 /></button>
          <button className="red" onClick={() => setUrgency("overdue")}><span>已超时</span><strong>{overdue}</strong><small>需要立即处理</small><AlertTriangle /></button>
          <button className="green" onClick={() => setStatus("已完成")}><span>平台已同步</span><strong>{synced}</strong><small>完成并进入回写结果</small><CheckCircle2 /></button>
        </section>

        <section className="serviceTicketPanel">
          <header><div><h2>聚合工单队列</h2><p>待处理 {pending} 条 · 按超时风险自动排序</p></div><div className="serviceTicketFilters"><label><Search /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="搜索平台、店铺、工单或订单" /></label><select value={urgency} onChange={(event) => setUrgency(event.target.value)}><option value="">全部时限</option><option value="overdue">已超时</option><option value="soon">即将超时</option><option value="watch">需关注</option><option value="normal">正常</option></select><select value={status} onChange={(event) => setStatus(event.target.value)}><option value="">全部状态</option><option>待分派</option><option>处理中</option><option>已完成</option></select></div></header>
          <div className="serviceTicketTableScroll"><table><thead><tr><th>平台 / 店铺</th><th>工单与订单</th><th>问题</th><th>负责人</th><th>超时提醒</th><th>处理状态</th><th>平台结果</th><th>操作</th></tr></thead><tbody>{visibleRows.map((row) => <tr key={row.id} className={urgencyOf(row)} onClick={() => setSelected(row)}><td><span className={`ticketPlatform ${platformOf(row)}`}>{platformOf(row)}</span><strong>{storeOf(row)}</strong></td><td><strong>{text(row.ticket)}</strong><small>{text(row.order)}</small></td><td><strong>{text(row.type)}</strong><small>{text(row.summary)}</small></td><td><UserRound /><span>{text(row.agent)}</span></td><td><b className={`ticketUrgency ${urgencyOf(row)}`}>{urgencyLabel(row)}</b><small>{text(row.deadline)}</small></td><td><span>{text(row.status)}</span></td><td><span className={`ticketSync ${syncLabel(row) === "已同步" ? "done" : ""}`}>{syncLabel(row)}</span></td><td onClick={(event) => event.stopPropagation()}>{text(row.status) === "已完成" ? <button className="ticketAction done" disabled><CheckCircle2 />已完成</button> : text(row.status) === "待分派" ? <button className="ticketAction" onClick={() => startTicket(row)}>开始处理</button> : <button className="ticketAction primary" onClick={() => completeAndSync(row)}><Send />完成并回写</button>}</td></tr>)}</tbody></table>{!visibleRows.length && <div className="operationsEmpty">当前筛选条件下没有工单</div>}</div>
        </section>
      </div>

      {selected && <div className="modalLayer" onMouseDown={(event) => event.target === event.currentTarget && setSelected(null)}><section className="serviceTicketModal"><header><div><strong>{text(selected.ticket)}</strong><span>{platformOf(selected)} · {storeOf(selected)}</span></div><button className="iconButton" aria-label="关闭" onClick={() => setSelected(null)}><X /></button></header><div className="serviceTicketModalBody"><section><span>问题摘要</span><p>{text(selected.summary)}</p><div><span>工单类型</span><strong>{text(selected.type)}</strong></div><div><span>关联订单</span><strong>{text(selected.order)}</strong></div></section><aside><div><span>超时状态</span><strong className={urgencyOf(selected)}>{urgencyLabel(selected)} · {text(selected.deadline)}</strong></div><div><span>负责人</span><strong>{text(selected.agent)}</strong></div><div><span>处理状态</span><strong>{text(selected.status)}</strong></div><div><span>平台结果</span><strong>{syncLabel(selected)}</strong></div></aside></div><footer><button className="button" onClick={() => setSelected(null)}>关闭</button>{text(selected.status) === "待分派" && <button className="button" onClick={() => startTicket(selected)}>开始处理</button>}{text(selected.status) !== "已完成" && text(selected.status) !== "待分派" && <button className="button primary" onClick={() => completeAndSync(selected)}><Send />完成并回写平台</button>}</footer></section></div>}
      {notice && <button className="toast" onClick={() => setNotice("")}>{notice}</button>}
    </main>
  );
}
